//
// DISCLAIMER
//
// Copyright 2026 ArangoDB GmbH, Cologne, Germany
//

import styled from "@emotion/styled";
import { Grid, Header, Label, Segment } from "semantic-ui-react";
import { RunAudit } from "../../api/cd-robot/v1/cd_robot";
import { Field, FieldContent, FieldLabelWide } from "../../ui/lib";
import { FormattedDateWithLabel, DateTimeTransformationTypes } from "../../util/dateAndTimeUtils/DateTime";
import { actorLabel, formatDuration, hasRunAudit, runKindLabel, runOutcomeColor, runOutcomeLabel, timestampToIso } from "./auditDisplay";

const Mono = styled.span`
  font-family: "Consolas", "Monaco", monospace;
  font-size: 0.92em;
`;

interface RunAuditCardProps {
  title: string;
  run?: RunAudit;
  inProgress?: boolean;
}

export const RunAuditCard = ({ title, run, inProgress }: RunAuditCardProps) => {
  if (!hasRunAudit(run)) {
    return null;
  }

  const startedIso = timestampToIso(run?.started_at);
  const endedIso = timestampToIso(run?.ended_at);
  const duration = formatDuration(run?.started_at, run?.ended_at);

  return (
    <Segment>
      <Header sub>
        {title}
        {!inProgress && (
          <Label color={runOutcomeColor(run?.outcome)} style={{ marginLeft: "0.5rem" }}>
            {runOutcomeLabel(run?.outcome)}
          </Label>
        )}
        {inProgress && (
          <Label color="blue" style={{ marginLeft: "0.5rem" }}>
            In progress
          </Label>
        )}
      </Header>
      <Grid stackable columns={2}>
        <Grid.Column>
          <Field>
            <FieldLabelWide>Run ID</FieldLabelWide>
            <FieldContent>
              <Mono>{run?.run_id}</Mono>
            </FieldContent>
          </Field>
          <Field>
            <FieldLabelWide>Kind</FieldLabelWide>
            <FieldContent>{runKindLabel(run?.run_kind)}</FieldContent>
          </Field>
          {run?.branch && (
            <Field>
              <FieldLabelWide>Branch</FieldLabelWide>
              <FieldContent>
                <Mono>{run.branch}</Mono>
              </FieldContent>
            </Field>
          )}
          {run?.mode && (
            <Field>
              <FieldLabelWide>Mode</FieldLabelWide>
              <FieldContent>{run.mode}</FieldContent>
            </Field>
          )}
        </Grid.Column>
        <Grid.Column>
          {startedIso && (
            <Field>
              <FieldLabelWide>Started</FieldLabelWide>
              <FieldContent>
                <FormattedDateWithLabel dateTime={startedIso} transformTo={DateTimeTransformationTypes.LOCAL} />
              </FieldContent>
            </Field>
          )}
          {endedIso && (
            <Field>
              <FieldLabelWide>Ended</FieldLabelWide>
              <FieldContent>
                <FormattedDateWithLabel dateTime={endedIso} transformTo={DateTimeTransformationTypes.LOCAL} />
              </FieldContent>
            </Field>
          )}
          {duration && (
            <Field>
              <FieldLabelWide>Duration</FieldLabelWide>
              <FieldContent>{duration}</FieldContent>
            </Field>
          )}
          <Field>
            <FieldLabelWide>Started by</FieldLabelWide>
            <FieldContent>{actorLabel(run?.started_by_id, run?.started_by_email)}</FieldContent>
          </Field>
          {(run?.aborted_by_email || run?.aborted_by_id) && (
            <Field>
              <FieldLabelWide>Aborted by</FieldLabelWide>
              <FieldContent>{actorLabel(run?.aborted_by_id, run?.aborted_by_email)}</FieldContent>
            </Field>
          )}
        </Grid.Column>
      </Grid>
      {run?.failure_summary && (
        <Field>
          <FieldLabelWide>Failure</FieldLabelWide>
          <FieldContent>{run.failure_summary}</FieldContent>
        </Field>
      )}
    </Segment>
  );
};
